import React, { useEffect, useRef } from "react";
import { Button, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Tournament } from "@nestcoinco/hyper-api-gateway-api-client";

import "./TournamentTile.scss";
import MegaphoneIcon from "../../icons/MegaphoneIcon";
import DotIcon from "../../icons/DotIcon";
import HyperCoin from "../../HyperCoin/HyperCoin";
import LiveMultiTournamentsDialog from "../LiveMultiTournamentsDialog/LiveMultiTournamentsDialog";
import useDisclosure from "../../../hooks/useDisclosure";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface TournamentTileProps {
  tournaments: Tournament[];
  handlePlayTournament: () => void;
}

interface SingleTournamentTileProps extends Pick<
  TournamentTileProps,
  "handlePlayTournament"
> {
  tournamentDetails: Tournament;
}

const renderFiatAmount = (fiatValue: string) => {
  return (
    <Stack direction="horizontal" style={{ gap: 2 }}>
      <span>{fiatValue.charAt(0)}</span>
      <span>{fiatValue.slice(1)}</span>
    </Stack>
  );
};

function SingleTournamentTile({
  tournamentDetails,
  handlePlayTournament,
}: SingleTournamentTileProps) {
  const { t } = useTranslation("games");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const minReward = isFiatDisplay
    ? renderFiatAmount(formatHprToFiatValue(tournamentDetails.minimumRewardAmount))
    : tournamentDetails.minimumRewardAmount;
  const maxReward = isFiatDisplay
    ? renderFiatAmount(formatHprToFiatValue(tournamentDetails.rewardAmount))
    : tournamentDetails.rewardAmount;

  return (
    <Stack
      direction="horizontal"
      gap={1}
      className="justify-content-between SingleTournamentTile"
      onClick={handlePlayTournament}
    >
      <MegaphoneIcon style={{ flex: 1 }} />
      <p className="nowrap Grotesk SpecialFontSettings">
        {t("live_tournament")}
      </p>
      <DotIcon />
      <div style={{ overflowX: "hidden" }}>
        <Stack direction="horizontal" className="nowrap PrizePool">
          <p className="Grotesk SpecialFontSettings">{t("prize_pool")}:</p>
          {!isFiatDisplay && <HyperCoin width={8.3} height={8.3} />}
          <p className="Grotesk SpecialFontSettings">{minReward}</p>-
          {!isFiatDisplay && <HyperCoin width={8.3} height={8.3} />}
          <p className="Grotesk SpecialFontSettings">{maxReward}</p>
        </Stack>
      </div>
      <Button
        className="fw-bold Grotesk SpecialFontSettings JoinCta"
        onClick={handlePlayTournament}
      >
        {t("join")}
      </Button>
    </Stack>
  );
}

function MultiTournamentTile({
  tournaments,
  handlePlayTournament,
}: TournamentTileProps) {
  const { t } = useTranslation("games");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const prizePoolSliderRef = useRef<HTMLDivElement>(null);
  const {
    isOpen: isLiveMultiTournamentsOpen,
    onOpen: openLiveMultiTournaments,
    onClose: closeLiveMultiTournaments,
  } = useDisclosure();

  useEffect(() => {
    const prizePoolSlider = prizePoolSliderRef.current;
    if (prizePoolSlider) {
      prizePoolSlider.style.setProperty(
        "--tournament-count",
        tournaments.length.toString()
      );
    }
  }, [tournaments.length]);

  return (
    <>
      <Stack direction="horizontal" gap={1} className="MultiTournamentTile">
        <Stack
          as="button"
          direction="horizontal"
          gap={1}
          className="TournamentEntries"
          onClick={openLiveMultiTournaments}
        >
          <MegaphoneIcon />
          <p
            className="Grotesk SpecialFontSettings"
            style={{ minWidth: "max-content" }}
          >
            {t("entries_count_key", { count: tournaments.length })}
          </p>
        </Stack>
        <Stack direction="horizontal" gap={1} className="PrizePool">
          <span
            className="Grotesk SpecialFontSettings"
            style={{ minWidth: "max-content" }}
          >
            {t("prize_pool")}:
          </span>
          <div className="Slider" ref={prizePoolSliderRef}>
            <div className="SlideTrack">
              {tournaments.map((tournament) => (
                <Stack
                  key={`${tournament.id} prize pool`}
                  direction="horizontal"
                  gap={1}
                  className="Slide"
                >
                  <p className="d-inline-flex align-items-center justify-content-center Grotesk SpecialFontSettings Reward">
                    {!isFiatDisplay && <HyperCoin width={8.3} height={8.3} />}
                    {isFiatDisplay
                      ? renderFiatAmount(formatHprToFiatValue(tournament.minimumRewardAmount))
                      : tournament.minimumRewardAmount}
                  </p>
                  <span>-</span>
                  <p className="d-inline-flex align-items-center justify-content-center Grotesk SpecialFontSettings Reward">
                    {!isFiatDisplay && <HyperCoin width={8.3} height={8.3} />}
                    {isFiatDisplay
                      ? renderFiatAmount(formatHprToFiatValue(tournament.rewardAmount))
                      : tournament.rewardAmount}
                  </p>
                </Stack>
              ))}
            </div>
          </div>
          <Button
            className="fw-bold Grotesk SpecialFontSettings JoinCta"
            onClick={openLiveMultiTournaments}
          >
            {t("join")}
          </Button>
        </Stack>
      </Stack>

      <LiveMultiTournamentsDialog
        isOpen={isLiveMultiTournamentsOpen}
        onClose={closeLiveMultiTournaments}
        tournaments={tournaments}
        handlePlayTournament={handlePlayTournament}
      />
    </>
  );
}

export default function TournamentTile({
  tournaments,
  handlePlayTournament,
}: TournamentTileProps) {
  return (
    <>
      {tournaments.length > 1 ? (
        <MultiTournamentTile
          tournaments={tournaments}
          handlePlayTournament={handlePlayTournament}
        />
      ) : (
        <SingleTournamentTile
          tournamentDetails={tournaments[0]}
          handlePlayTournament={handlePlayTournament}
        />
      )}
    </>
  );
}